import React from 'react';
import { db } from '@/vars/firebase';
import { downloadCSV } from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { get, set } from 'lodash';
import { TRANSACTION } from '@/vars';
import Typography from '@material-ui/core/Typography';
import { ErrorDetailList } from '../vars/templates';

// involve db data retrieve
export const checkMachineDualMode = async clientId => {
  try {
    const machineSnaps = await db
      .collection('machines')
      .where('client.id', '==', clientId)
      .get();

    const doc = machineSnaps.docs.find(doc => {
      const data = doc.data();
      return get(data, 'machineParams.machineDualMode') === true || get(data, 'machineParams.mroDualMode') === true;
    });

    return Boolean(doc);
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const getEmployeeDocs = async clientId => {
  try {
    const employeeSnaps = await db
      .collection('employees')
      .where('client', '==', clientId)
      .get();

    if (employeeSnaps.empty) throw new Error('no employee found.');

    const employees = employeeSnaps.docs.map(doc => {
      const id = doc.id;
      return { ...doc.data(), id };
    });

    return employees;
  } catch (e) {
    throw e;
  }
};

export const getProductDocs = async clientId => {
  try {
    const productSnaps = await db
      .collection('products')
      .where('client', '==', clientId)
      .get();

    if (productSnaps.empty) throw new Error('no products found.');

    const products = productSnaps.docs.map(doc => {
      const id = doc.id;
      return { ...doc.data(), id };
    });

    return products;
  } catch (e) {
    throw e;
  }
};

export const getProductCategoryDocs = async clientId => {
  try {
    const categorySnaps = await db
      .collection('categories')
      .where('client', '==', clientId)
      .get();

    if (categorySnaps.empty) throw new Error('no product categories found.');

    const categories = categorySnaps.docs.map(doc => {
      const id = doc.id;
      return { ...doc.data(), id };
    });

    return categories;
  } catch (err) {
    throw err;
  }
};

// format date/time in Malay (ms-MY) or English (en-MY)

export const renderDateMY = (record, { locale = 'en-MY' } = {}) => {
  const d = get(record, 'date');
  if (!(d instanceof Date)) return 'N/A';
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'Asia/Kuala_Lumpur',
    weekday: 'short', // “Sel” for Selasa or “Tue” if using en-MY
    day: 'numeric', // e.g. “29”
    month: 'long', // e.g. “September” / “September”
    year: 'numeric', // e.g. “2020”
  }).format(d);
};

export const renderTimeMY = (record, { locale = 'en-MY' } = {}) => {
  const d = get(record, 'date');
  if (!(d instanceof Date)) return 'N/A';
  // hour12: false → 24-hour clock (common in Malaysia)
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'Asia/Kuala_Lumpur',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d);
};

// payment related
export const getTransactionId = record => {
  if (get(record, 'payment.transactionId')) {
    return get(record, 'payment.transactionId');
  }

  if (get(record, 'payment.ghlPaymentDetail.paymentResponse.msg.txnRef')) {
    return get(record, 'payment.ghlPaymentDetail.paymentResponse.msg.txnRef');
  }
  return '';
};

export const getReferenceNo = record => {
  if (get(record, 'payment.refNo')) {
    return get(record, 'payment.refNo');
  }

  if (get(record, 'payment.ghlPaymentDetail.paymentResponse.msg.retTxnRef')) {
    return get(record, 'payment.ghlPaymentDetail.paymentResponse.msg.retTxnRef');
  }
  return '';
};

export const getPaymentType = post => {
  try {
    if (get(post, 'payment.gatewayID')) {
      return TRANSACTION.GATEWAY[`${get(post, 'payment.gatewayID')}`];
    }
    if (get(post, 'payment.cash')) {
      return 'Cash Coin';
    }
    if (get(post, 'payment.terminal')) {
      return 'Paywave';
    }
    if (get(post, 'payment.ghlPaymentDetail.paymentResponse.msg.productCode')) {
      return get(post, 'payment.ghlPaymentDetail.paymentResponse.msg.productCode');
    }

    if (get(post, 'payment.virclePaymentDetail')) {
      return 'Vircle';
    }

    if (get(post, 'payment.onePay')) {
      return '1Pay';
    }

    if (get(post, 'mode') == 'mro' || get(post, 'payment.paymentType') == 'mro') {
      return get(post, 'payment.paymentType') || 'mro';
    }

    return '';
  } catch (error) {
    return '';
  }
};

// mro employee related
export const getEmployee = (post, employees) => {
  try {
    const employeeId = get(post, 'employee.id');
    const matchDoc = employees.find(doc => doc.id === employeeId);

    const fetchData = {
      department: get(matchDoc, 'type.name'),
      cardNumber: get(matchDoc, 'cardNumber'),
    };

    return fetchData;
  } catch (err) {
    return {
      department: null,
      cardNumber: null,
    };
  }
};

// product related
export const getProduct = (post, products) => {
  try {
    const productId = get(post, 'product.id');

    const matchDoc = products.find(doc => doc.id === productId);

    const fetchData = {
      sku: get(matchDoc, 'sku'),
      serialNumber: get(matchDoc, 'serialNumber'),
      priceCash: get(matchDoc, 'priceCash'),
      priceCredit: get(matchDoc, 'priceCredit'),
    };

    return fetchData;
  } catch (err) {
    return {};
  }
};

export const getProductCategory = (post, categories) => {
  try {
    const categoryId = get(post, 'product.categoryId');

    const matchDoc = categories.find(doc => doc.id === categoryId);

    const fetchData = {
      category: get(matchDoc, 'name'),
    };

    return fetchData;
  } catch (err) {
    return {
      category: null,
    };
  }
};

export const getFormattedSlotId = post => {
  const slotId = get(post, 'product.slot') || get(post, 'product.slotId');
  if (!slotId) return '';

  var rowArray = ['A', 'B', 'C', 'D', 'E', 'F'];
  var row = Math.floor(slotId / 12);
  var column = slotId % 12;
  return `${rowArray[row]}${column}`;
};

// transaction status
export const getStatus = post => {
  const status = get(post, 'status');
  switch (status) {
    case 1:
      return 'Successful';
    case -1:
      return 'Canceled';
    case -2:
      return 'Dropped';
    case -3:
      return 'Void';
    case -4:
      return 'DispensingError';
    default:
      return 'Failed';
  }
};

export const renderTransactionStatus = record => {
  switch (get(record, 'status')) {
    case 1:
      return <Typography style={{ color: '#00C07F', fontSize: 12 }}>Successful </Typography>;
    case 0:
      return <Typography style={{ color: '#FF6562', fontSize: 12 }}>Failed</Typography>;
    case -1:
      return <Typography style={{ color: '#C0C0C0', fontSize: 12 }}>Canceled</Typography>;
    case -2:
      return <Typography style={{ color: '#FF6562', fontSize: 12 }}>Dropped</Typography>;
    case -3:
      return <Typography style={{ color: '#FF6562', fontSize: 12 }}>Void</Typography>;
    case -4:
      return <Typography style={{ color: '#FF6562', fontSize: 12 }}>DispensingEr</Typography>;
    default:
      return <Typography style={{ fontSize: 12 }}>Others</Typography>;
  }
};

// error related
export const getErrorCategoryMessage = post => {
  var errorDescription = get(post, 'error.description');

  if (!errorDescription) return { category: '', message: '' };

  const desc = errorDescription.toLowerCase();

  const matchDetail = ErrorDetailList.find(err => err.key.some(k => desc.includes(k.toLowerCase())));

  if (!matchDetail) return { category: '', message: '' };

  const isDriverBoardKey = matchDetail.key.includes('driver board went wrong' || 'no item detect dropped');

  if (isDriverBoardKey) {
    const driverBoardResponse = get(post, 'responseStatus', {});

    if (!driverBoardResponse) {
      return {
        category: matchDetail.category,
        message: matchDetail.message,
      };
    }

    const { globalDroppedSensor, itemDroppedSensor, motorTurned, itemDropped } = driverBoardResponse;

    if (!globalDroppedSensor && !itemDroppedSensor) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor off`,
      };
    }
    if (!motorTurned) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — motor didn’t spin`,
      };
    }
    if (!itemDropped) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor didn’t detect drop`,
      };
    }
  }

  return {
    category: matchDetail.category,
    message: matchDetail.message,
  };
};

export const getSanitizedErrorDetail = value => {
  var errorDescription = get(value, 'error.description', '') || '';
  errorDescription = `${errorDescription
    .replaceAll('com.vechnology.ve.models.payment.', '*')
    .replaceAll('com.vechnology.ve.models.serial.driverBoard.', '*')
    .replaceAll('com.vechnology.ve', '*')
    .substring(0, 100)}...`;
  return errorDescription;
};

// Others Action
export const setData = (post, path, value) => {
  set(post, path, value);
};

// export MRO transaction as csv file
export const exportCSV = (datas, template) => {
  let filteredData, headers, rename;
  if (template) {
    headers = template.map(col => col.field);
    rename = template.map(col => col.label);

    // 1) Create a new array which only has the template’s fields:
    filteredData = datas.map(row =>
      headers.reduce((obj, path) => {
        // use lodash/get so you can handle nested paths like 'machine.machineUUID'
        obj[path] = get(row, path, '');
        return obj;
      }, {})
    );
  } else {
    // —–– Admin mode: flatten and export every key
    filteredData = datas.map(row => flattenObject(row));

    // gather all unique keys in order of first appearance
    headers = Array.from(new Set(filteredData.flatMap(Object.keys)));
    // use the same for rename so header titles match keys
    rename = headers;
  }

  jsonExport(filteredData, { headers, rename }, (err, csv) => {
    downloadCSV(csv, 'Transaction Report');
  });
};

/**
 * Recursively flatten an object:
 * { a: { b: 1 }, c: 2 } → { 'a.b': 1, c: 2 }
 */
function flattenObject(obj, prefix = '', result = {}) {
  Object.entries(obj).forEach(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      flattenObject(val, path, result);
    } else {
      result[path] = val;
    }
  });
  return result;
}
